/**
 * Maths pasted from elsewhere becomes Lilia maths (18 Oct; the user: "paste from many sources").
 *
 * What a copy carries, and what is read from it, before the editor parses the paste:
 *   • KaTeX (ChatGPT, Claude, Notion, many sites): the TeX itself, in its MathML annotation;
 *   • MathJax 2: the TeX, in <script type="math/tex">; MathJax 3: its MathML;
 *   • Wikipedia: the TeX, in the MathML annotation or the image's alt text;
 *   • MathML (Word's "Copy as MathML", browsers, Mathematica…): converted to LaTeX;
 *   • Word's own equations (OMML, in the copy's conditional comments): converted to LaTeX.
 * Inline maths becomes inline maths; a display becomes an equation block. Lilia's own maths is
 * left alone (it already parses). No AI: a photo, or text like "x² + y²", goes to Ask Lilia.
 */

const INLINE_TAG = (latex: string) => `<span data-type="math" data-latex="${escapeAttr(latex)}"></span>`;
const DISPLAY_TAG = (latex: string) => `<div data-type="equation-block" latex="${escapeAttr(latex)}"></div>`;

function escapeAttr(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** True when the HTML may hold maths worth converting (cheap test before parsing). */
export function mayHoldMath(html: string): boolean {
  return /<math[\s>]|class="[^"]*katex|math\/tex|mwe-math|msEquation|<mjx-container|\{\\displaystyle/i.test(html);
}

/** The pasted HTML with every formula in it replaced by Lilia's maths; unchanged when there is none. */
export function convertPastedMath(html: string): string {
  if (!html || !mayHoldMath(html)) return html;
  const source = convertWordEquations(html);
  const doc = new DOMParser().parseFromString(source, "text/html");
  let changed = source !== html;

  const replace = (el: Element, latex: string | null, display: boolean) => {
    if (latex == null || !latex.trim()) return;
    const tpl = doc.createElement("template");
    tpl.innerHTML = display ? DISPLAY_TAG(latex.trim()) : INLINE_TAG(latex.trim());
    el.replaceWith(tpl.content);
    changed = true;
  };
  const inLilia = (el: Element) => !!el.parentElement?.closest('[data-type="math"],[data-type="equation-block"],[data-latex]');

  // KaTeX: the display wrapper, or the formula itself.
  for (const el of Array.from(doc.querySelectorAll(".katex-display, .katex"))) {
    if (!el.isConnected || inLilia(el)) continue;
    if (el.classList.contains("katex") && el.parentElement?.closest(".katex-display")) continue;
    const tex = el.querySelector('annotation[encoding="application/x-tex"]')?.textContent ?? null;
    replace(el, tex, el.classList.contains("katex-display"));
  }
  // MathJax 2: the TeX next to its rendering.
  for (const el of Array.from(doc.querySelectorAll('script[type^="math/tex"]'))) {
    const type = el.getAttribute("type") ?? "";
    el.parentElement?.querySelectorAll(".MathJax, .MathJax_Display, .MathJax_Preview").forEach((r) => r.remove());
    replace(el, el.textContent, /mode=display/.test(type));
  }
  // MathJax 3: its assistive MathML.
  for (const el of Array.from(doc.querySelectorAll("mjx-container"))) {
    const math = el.querySelector("math");
    replace(el, math ? mathmlToLatex(math) : null, el.getAttribute("display") === "true" || math?.getAttribute("display") === "block");
  }
  // Wikipedia: the TeX in its annotation or alt text; the fallback image goes.
  for (const el of Array.from(doc.querySelectorAll(".mwe-math-element"))) {
    if (!el.isConnected) continue;
    const math = el.querySelector("math");
    const alt = math?.getAttribute("alttext") ?? el.querySelector("img")?.getAttribute("alt") ?? null;
    const tex = math?.querySelector('annotation[encoding="application/x-tex"]')?.textContent ?? alt;
    const display = math?.getAttribute("display") === "block" || /mwe-math-element--display|mwe-math-fallback-image-display/.test(el.outerHTML.slice(0, 400));
    replace(el, tex ? stripDisplaystyle(tex) : null, display);
  }
  for (const img of Array.from(doc.querySelectorAll("img[alt]"))) {
    const alt = img.getAttribute("alt") ?? "";
    if (/^\s*\{\\displaystyle/.test(alt)) replace(img, stripDisplaystyle(alt), /display/.test(img.className));
  }
  // Any MathML left.
  for (const el of Array.from(doc.querySelectorAll("math"))) {
    if (!el.isConnected || inLilia(el)) continue;
    replace(el, mathmlToLatex(el), el.getAttribute("display") === "block");
  }

  return changed ? doc.body.innerHTML : html;
}

/** "{\displaystyle x^{2}}" → "x^{2}" (Wikipedia's alt text). */
export function stripDisplaystyle(tex: string): string {
  const m = tex.trim().match(/^\{\\displaystyle\s*([\s\S]*)\}$/);
  return (m ? m[1] : tex).trim();
}

// ── Characters ─────────────────────────────────────────────────────────────

const SYMBOLS: Record<string, string> = {
  "≤": "\\le", "≥": "\\ge", "≠": "\\neq", "±": "\\pm", "∓": "\\mp", "×": "\\times", "·": "\\cdot", "⋅": "\\cdot",
  "÷": "\\div", "→": "\\to", "←": "\\leftarrow", "↔": "\\leftrightarrow", "⇒": "\\Rightarrow", "⇐": "\\Leftarrow",
  "⇔": "\\Leftrightarrow", "↦": "\\mapsto", "∞": "\\infty", "∑": "\\sum", "∏": "\\prod", "∫": "\\int", "∬": "\\iint",
  "∮": "\\oint", "∂": "\\partial", "∇": "\\nabla", "∈": "\\in", "∉": "\\notin", "∋": "\\ni", "⊂": "\\subset",
  "⊆": "\\subseteq", "⊃": "\\supset", "⊇": "\\supseteq", "∪": "\\cup", "∩": "\\cap", "∅": "\\emptyset", "∀": "\\forall",
  "∃": "\\exists", "¬": "\\neg", "∧": "\\wedge", "∨": "\\vee", "≈": "\\approx", "≡": "\\equiv", "∼": "\\sim",
  "≃": "\\simeq", "≅": "\\cong", "∝": "\\propto", "…": "\\ldots", "⋯": "\\cdots", "⋮": "\\vdots", "⋱": "\\ddots",
  "′": "'", "″": "''", "∘": "\\circ", "⊗": "\\otimes", "⊕": "\\oplus", "†": "\\dagger", "ℏ": "\\hbar", "ℓ": "\\ell",
  "ℝ": "\\mathbb{R}", "ℕ": "\\mathbb{N}", "ℤ": "\\mathbb{Z}", "ℚ": "\\mathbb{Q}", "ℂ": "\\mathbb{C}", "√": "\\surd",
  "⟨": "\\langle", "⟩": "\\rangle", "〈": "\\langle", "〉": "\\rangle", "‖": "\\|", "∣": "\\mid", "−": "-", "∗": "\\ast",
  "°": "^{\\circ}", "≪": "\\ll", "≫": "\\gg", "⊥": "\\perp", "∥": "\\parallel", "∠": "\\angle", "△": "\\triangle",
  "{": "\\{", "}": "\\}", "%": "\\%", "#": "\\#", "&": "\\&", "_": "\\_",
  "\u2061": "", "\u2062": "", "\u2063": ",", "\u2064": "+", "\u00a0": " ", "\u200b": "",
};
const GREEK: Record<string, string> = {
  α: "alpha", β: "beta", γ: "gamma", δ: "delta", ε: "epsilon", ϵ: "epsilon", ζ: "zeta", η: "eta", θ: "theta",
  ϑ: "vartheta", ι: "iota", κ: "kappa", λ: "lambda", μ: "mu", ν: "nu", ξ: "xi", π: "pi", ϖ: "varpi", ρ: "rho",
  ϱ: "varrho", σ: "sigma", ς: "varsigma", τ: "tau", υ: "upsilon", φ: "phi", ϕ: "phi", χ: "chi", ψ: "psi", ω: "omega",
  Γ: "Gamma", Δ: "Delta", Θ: "Theta", Λ: "Lambda", Ξ: "Xi", Π: "Pi", Σ: "Sigma", Υ: "Upsilon", Φ: "Phi", Ψ: "Psi", Ω: "Omega",
};
const FUNCTIONS = new Set(["sin", "cos", "tan", "cot", "sec", "csc", "arcsin", "arccos", "arctan", "sinh", "cosh", "tanh",
  "log", "ln", "lg", "exp", "lim", "max", "min", "sup", "inf", "det", "dim", "ker", "deg", "gcd", "arg", "Pr", "liminf", "limsup"]);
const BIG = new Set(["∑", "∏", "∫", "∬", "∮", "⋃", "⋂", "lim", "max", "min", "sup", "inf"]);

/** Unicode maths text as LaTeX, a character at a time. */
export function charsToLatex(text: string): string {
  let out = "";
  for (const ch of text) {
    if (GREEK[ch]) out += `\\${GREEK[ch]} `;
    else if (ch in SYMBOLS) out += SYMBOLS[ch] + (/^\\[a-zA-Z]+$/.test(SYMBOLS[ch]) ? " " : "");
    else out += ch;
  }
  return out;
}

function identifier(text: string, variant?: string | null): string {
  const t = text.trim();
  if (FUNCTIONS.has(t)) return `\\${t} `;
  const body = charsToLatex(t).trim();
  const wrap = { "double-struck": "mathbb", bold: "mathbf", script: "mathcal", fraktur: "mathfrak", "sans-serif": "mathsf", normal: "mathrm" }[variant ?? ""];
  if (wrap && !(variant === "normal" && t.length === 1 && /[^a-zA-Z]/.test(t))) return `\\${wrap}{${body}}`;
  return [...t].length > 1 && /^[a-zA-Z]+$/.test(t) ? `\\mathrm{${t}}` : body;
}

function group(s: string): string {
  const t = s.trim();
  return [...t].length === 1 || /^\\[a-zA-Z]+$/.test(t) ? t : `{${t}}`;
}

const ACCENTS: Record<string, string> = {
  "^": "hat", "ˆ": "hat", "\u0302": "hat", "¯": "overline", "‾": "overline", "\u0305": "overline", "→": "vec", "\u20d7": "vec",
  "~": "tilde", "˜": "tilde", "\u0303": "tilde", "˙": "dot", "\u0307": "dot", "¨": "ddot", "\u0308": "ddot", "ˇ": "check",
};
const OPEN = new Set(["(", "[", "{", "|", "‖", "⟨", "〈", "⌊", "⌈"]);
const CLOSE = new Set([")", "]", "}", "|", "‖", "⟩", "〉", "⌋", "⌉"]);
const FENCE: Record<string, string> = { "{": "\\{", "}": "\\}", "⟨": "\\langle", "〈": "\\langle", "⟩": "\\rangle", "〉": "\\rangle",
  "‖": "\\|", "⌊": "\\lfloor", "⌋": "\\rfloor", "⌈": "\\lceil", "⌉": "\\rceil", "": "." };
const fence = (c: string) => FENCE[c] ?? c;

// ── MathML ─────────────────────────────────────────────────────────────────

const local = (el: Element) => el.localName.toLowerCase().replace(/^m:/, "");

/** MathML (a <math> element, or any part of one) as LaTeX. */
export function mathmlToLatex(el: Element): string {
  return tidy(mml(el));
}

function mml(el: Element): string {
  const kids = Array.from(el.children);
  const k = (i: number) => (kids[i] ? mml(kids[i]) : "");
  switch (local(el)) {
    case "math": case "mrow": case "mstyle": case "mpadded": case "menclose": case "merror": case "mtd":
      return row(kids);
    case "semantics": {
      const tex = el.querySelector('annotation[encoding="application/x-tex"], annotation[encoding="TeX"]');
      return tex?.textContent ? tex.textContent : k(0);
    }
    case "annotation": case "annotation-xml": case "mphantom": case "none": return "";
    case "mi": return identifier(el.textContent ?? "", el.getAttribute("mathvariant"));
    case "mn": return el.textContent?.trim() ?? "";
    case "mo": { const t = (el.textContent ?? "").trim(); return FUNCTIONS.has(t) ? `\\${t} ` : charsToLatex(t); }
    case "mtext": { const t = el.textContent ?? ""; return t.trim() ? `\\text{${t}}` : " "; }
    case "ms": return `\\text{"${el.textContent ?? ""}"}`;
    case "mspace": return "\\,";
    case "msup": return `${group(k(0))}^${group(k(1))}`;
    case "msub": return `${group(k(0))}_${group(k(1))}`;
    case "msubsup": return `${group(k(0))}_${group(k(1))}^${group(k(2))}`;
    case "mfrac":
      return el.getAttribute("linethickness") === "0" ? `\\binom{${k(0)}}{${k(1)}}` : `\\frac{${k(0)}}{${k(1)}}`;
    case "msqrt": return `\\sqrt{${row(kids)}}`;
    case "mroot": return `\\sqrt[${k(1)}]{${k(0)}}`;
    case "mover": return over(kids[0], kids[1], el.getAttribute("accent") === "true");
    case "munder": {
      const base = (kids[0]?.textContent ?? "").trim();
      if (BIG.has(base) || FUNCTIONS.has(base)) return `${k(0)}_${group(k(1))}`;
      const u = (kids[1]?.textContent ?? "").trim();
      if (u === "_" || u === "̲" || u === "‾") return `\\underline{${k(0)}}`;
      if (u === "⏟") return `\\underbrace{${k(0)}}`;
      return `\\underset{${k(1)}}{${k(0)}}`;
    }
    case "munderover": return `${group(k(0))}_${group(k(1))}^${group(k(2))}`;
    case "mfenced": {
      const open = el.getAttribute("open") ?? "(", close = el.getAttribute("close") ?? ")";
      const sep = el.getAttribute("separators") ?? ",";
      return `\\left${fence(open)} ${kids.map(mml).join(sep.trim() ? `${sep.trim()[0]} ` : " ")} \\right${fence(close)}`;
    }
    case "mtable": {
      const rows = kids.filter((r) => /^(mtr|mlabeledtr)$/.test(local(r)))
        .map((r) => Array.from(r.children).filter((c) => local(c) === "mtd").map((c) => mml(c).trim()).join(" & "));
      return `\\begin{matrix} ${rows.join(" \\\\ ")} \\end{matrix}`;
    }
    case "mtr": return kids.map(mml).join(" & ");
    case "mmultiscripts": return `${group(k(0))}_${group(k(1))}^${group(k(2))}`;
    default: return row(kids) || charsToLatex(el.textContent ?? "");
  }
}

function over(base: Element | undefined, top: Element | undefined, accent: boolean): string {
  const b = base ? mml(base) : "";
  const t = (top?.textContent ?? "").trim();
  const name = ACCENTS[t];
  if (name) return name === "overline" && !accent && [...(base?.textContent ?? "")].length > 1 ? `\\overline{${b}}` : `\\${name}{${b}}`;
  if (t === "⏞") return `\\overbrace{${b}}`;
  if (BIG.has((base?.textContent ?? "").trim())) return `${b}^${group(top ? mml(top) : "")}`;
  return `\\overset{${top ? mml(top) : ""}}{${b}}`;
}

/** A row; fences around something tall become \left … \right. */
function row(kids: Element[]): string {
  const parts = kids.map(mml);
  const first = kids[0], last = kids[kids.length - 1];
  if (kids.length >= 3 && first && last && local(first) === "mo" && local(last) === "mo") {
    const o = (first.textContent ?? "").trim(), c = (last.textContent ?? "").trim();
    const tall = kids.slice(1, -1).some((x) => /^(mfrac|mtable|munderover|msqrt|mroot)$/.test(local(x)) || x.querySelector("mfrac, mtable, munderover"));
    if (OPEN.has(o) && CLOSE.has(c) && tall)
      return `\\left${fence(o)} ${parts.slice(1, -1).join("")} \\right${fence(c)}`;
  }
  return parts.join("");
}

/** Tables inside ( ) or [ ] become pmatrix / bmatrix; spaces settle. */
function tidy(s: string): string {
  return s
    .replace(/\\left\( (\\begin\{matrix\}[\s\S]*?\\end\{matrix\}) \\right\)/g, (_m, inner: string) => inner.replace(/matrix/g, "pmatrix"))
    .replace(/\\left\[ (\\begin\{matrix\}[\s\S]*?\\end\{matrix\}) \\right\]/g, (_m, inner: string) => inner.replace(/matrix/g, "bmatrix"))
    .replace(/[ \t]{2,}/g, " ")
    .replace(/ +([_^}])/g, "$1")
    .trim();
}

// ── Word (OMML) ────────────────────────────────────────────────────────────

/**
 * Word puts each equation in a conditional comment, as OMML, with an image or text fallback for
 * other programs right after it. The equation replaces both.
 */
export function convertWordEquations(html: string): string {
  if (!/msEquation/i.test(html)) return html;
  return html
    .replace(/<!--\[if gte msEquation 12\]>([\s\S]*?)<!\[endif\]-->/gi, (_m, omml: string) => {
      // Word writes empty OMML elements as <m:deg/>, which an HTML parser opens and never closes.
      const closed = omml.replace(/<(m:[a-zA-Z]+)([^<>]*?)\/>/g, "<$1$2></$1>");
      const doc = new DOMParser().parseFromString(`<body>${closed}</body>`, "text/html");
      const para = Array.from(doc.body.getElementsByTagName("*")).find((e) => local(e) === "omathpara");
      const maths = Array.from(doc.body.getElementsByTagName("*")).filter((e) => local(e) === "omath");
      if (maths.length === 0) return "";
      const latex = maths.map((m) => tidy(omml2tex(m))).filter(Boolean);
      if (latex.length === 0) return "";
      return para
        ? latex.map(DISPLAY_TAG).join("")
        : latex.map(INLINE_TAG).join("");
    })
    .replace(/<!\[if !msEquation\]>[\s\S]*?<!\[endif\]>/gi, "");
}

function child(el: Element, name: string): Element | undefined {
  return Array.from(el.children).find((c) => local(c) === name);
}
function val(el: Element | undefined, prop: string): string | null {
  const p = el ? Array.from(el.getElementsByTagName("*")).find((c) => local(c) === prop) : undefined;
  return p ? (p.getAttribute("m:val") ?? p.getAttribute("val")) : null;
}

/** OMML as LaTeX. */
export function omml2tex(el: Element): string {
  const of = (name: string) => { const c = child(el, name); return c ? omml2tex(c) : ""; };
  const name = local(el);
  if (name.endsWith("pr")) return "";   // properties: rPr, fPr, naryPr, ctrlPr…
  switch (name) {
    case "r": {
      // The text is in <m:t>, or (Word's HTML) in the <i>/<span> it styles the run with.
      const t = Array.from(el.childNodes).filter((c) => !(c instanceof Element && local(c).endsWith("pr"))).map((c) => c.textContent ?? "").join("");
      const trimmed = t.trim();
      if (FUNCTIONS.has(trimmed)) return `\\${trimmed} `;
      return charsToLatex(t);
    }
    case "f": return val(el, "type") === "noBar" ? `\\binom{${of("num")}}{${of("den")}}` : `\\frac{${of("num")}}{${of("den")}}`;
    case "ssup": return `${group(of("e"))}^${group(of("sup"))}`;
    case "ssub": return `${group(of("e"))}_${group(of("sub"))}`;
    case "ssubsup": return `${group(of("e"))}_${group(of("sub"))}^${group(of("sup"))}`;
    case "rad": {
      const deg = of("deg").trim();
      return deg ? `\\sqrt[${deg}]{${of("e")}}` : `\\sqrt{${of("e")}}`;
    }
    case "nary": {
      const chr = val(child(el, "narypr"), "chr") ?? "∫";
      const op = charsToLatex(chr).trim();
      const sub = of("sub").trim(), sup = of("sup").trim();
      return `${op}${sub ? `_${group(sub)}` : ""}${sup ? `^${group(sup)}` : ""} ${of("e")}`;
    }
    case "d": {
      const pr = child(el, "dpr");
      const beg = val(pr, "begchr") ?? "(", end = val(pr, "endchr") ?? ")", sep = val(pr, "sepchr") ?? "|";
      const parts = Array.from(el.children).filter((c) => local(c) === "e").map(omml2tex);
      return `\\left${fence(beg)} ${parts.join(` ${charsToLatex(sep)} `)} \\right${fence(end)}`;
    }
    case "func": return `${of("fname").trim()} ${of("e")}`;
    case "acc": {
      const chr = val(child(el, "accpr"), "chr") ?? "\u0302";
      return `\\${ACCENTS[chr] ?? "hat"}{${of("e")}}`;
    }
    case "bar": return val(child(el, "barpr"), "pos") === "top" ? `\\overline{${of("e")}}` : `\\underline{${of("e")}}`;
    case "limlow": return `${of("e").trim()}_${group(of("lim"))}`;
    case "limupp": return `${of("e").trim()}^${group(of("lim"))}`;
    case "groupchr": return val(child(el, "groupchrpr"), "pos") === "top" ? `\\overbrace{${of("e")}}` : `\\underbrace{${of("e")}}`;
    case "m": {
      const rows = Array.from(el.children).filter((c) => local(c) === "mr")
        .map((r) => Array.from(r.children).filter((c) => local(c) === "e").map((c) => omml2tex(c).trim()).join(" & "));
      return `\\begin{matrix} ${rows.join(" \\\\ ")} \\end{matrix}`;
    }
    case "eqarr": {
      const rows = Array.from(el.children).filter((c) => local(c) === "e").map((c) => omml2tex(c).trim());
      return `\\begin{gathered} ${rows.join(" \\\\ ")} \\end{gathered}`;
    }
    default:
      return Array.from(el.children).map(omml2tex).join("");
  }
}
